import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import { useSetCategoryWinnerMutation } from '../rnrApiSlice';
import toast from 'react-hot-toast';
import { Chip } from '@mui/material';
import { Divider } from '@mui/material';
import { ArrowBack, ArrowForward, Category, Description, Group, Groups3, Person } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import AttachmentGallery from '../../../common/Utility/AttachmentGallery ';
import defaultimg from '../../../common/default.png';

const OverviewDialog = ({ open, handleClose, nomination }) => {
  const [winnerIdentifier, setWinnerIdentifier] = useState('');
  const [setCategoryWinner] = useSetCategoryWinnerMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    if (nomination.isTeamNomination) {
      setWinnerIdentifier(nomination.teamName);
    } else {
      setWinnerIdentifier(nomination.nomineeEmpCode);
    }
  }, [nomination]);

  const handleSetWinner = async (rnrId, categoryId, winnerIdentifier) => {
    try {
      await toast.promise(
        // Call the setCategoryWinner function with parameters and unwrap the result
        setCategoryWinner({ rnrId, categoryId, winnerIdentifier }).unwrap(),
        {
          loading: 'Setting winner...',
          success: 'Winner set successfully!',
          error: (err) => err.data?.message || 'Error setting winner. Please try again.',
        }
      );
      handleClose(); // Close dialog on success
    } catch (error) {
      console.error('Failed to set winner:', error);
      toast.error('Failed to set winner. Please try again.');
    }
  };

  const slideTransition = {
    TransitionComponent: Slide,
    TransitionProps: {
      direction: 'up',
    },
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      {...(isMobile ? slideTransition : {})}
      transitionDuration={300}
      keepMounted
      aria-describedby="slide-up animation"
    >
      <DialogTitle
        sx={{
          backgroundColor: 'white',
          color: 'black',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: '600',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '0.5px dotted lightgrey',
          marginBottom: '0.5rem',
        }}
      >
        Nomination Details
        <IconButton onClick={handleClose} size="small" sx={{ color: 'black' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{padding: isMobile ? '12px' : '24px', height: 'auto' }}>
        {nomination && (
          <>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography component="div" sx={{ marginRight: '6px', fontSize: isMobile ? '14px' : 'inherit' }}>
                Quarter Details :
              </Typography>
              <Chip label={`Year: ${nomination.year}`} sx={{ marginRight: '6px', fontSize: isMobile ? '12px' : 'inherit' }} />
              <Chip label={`Quarter: ${nomination.quarter}`} sx={{fontSize: isMobile ? '12px' : 'inherit'}}/>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {nomination.nominationCategory !== 'Team Work' && (
              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ marginRight: '6px', color: 'grey' }} />
                <Typography variant="body1" component="span" sx={{fontSize: isMobile ? '14px' : 'inherit'}}>
                  Nominee Name:
                  <Typography component="span" sx={{ marginLeft: '4px', fontSize: isMobile ? '14px' : 'inherit' }}>
                    {nomination.employeeName}
                  </Typography>
                </Typography>
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <Person sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{fontSize: isMobile ? '14px' : 'inherit'}}>
                Nominated by:
                <Typography component="span" sx={{ marginLeft: '4px', fontSize: isMobile ? '14px' : 'inherit' }}>
                  {nomination.nominatedBy}
                </Typography>
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <Category sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{fontSize: isMobile ? '14px' : 'inherit'}}>
                Category:
                <Typography component="span" sx={{ marginLeft: '4px', fontSize: isMobile ? '14px' : 'inherit' }}>
                  {nomination.nominationCategory}
                </Typography>
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {nomination.nominationCategory === 'Team Work' && (
              <Box display="flex" alignItems="center" mb={2}>
                <Group sx={{ marginRight: '6px', color: 'grey' }} />
                <Typography variant="body1" component="span" sx={{ fontSize: isMobile ? '14px' : 'inherit'}}>
                  Team Name:
                  <Typography component="span" sx={{ marginLeft: '4px', fontSize: isMobile ? '14px' : 'inherit'}}>
                    {nomination?.teamName}
                  </Typography>
                </Typography>
              </Box>
            )}

            {nomination.nominationCategory === 'Team Work' && (
              <Box display="flex" alignItems="center" mb={2} sx={{}}>
                <Groups3 sx={{ marginRight: '6px', color: 'grey' }} />
                <Typography variant="body1" component="div" sx={{ marginRight: '6px', fontSize: isMobile ? '14px' : 'inherit'}}>
                  Team Members :{' '}
                  <Chip
                    label={nomination.employeeName}
                    sx={{
                      marginRight: '6px',
                      marginBottom: '10px',
                    }}
                  />
                </Typography>
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="start" mb={2}>
              <Description sx={{ marginRight: '6px', color: 'grey' }} />
              <Typography variant="body1" component="span" sx={{fontSize: isMobile ? '14px' : 'inherit'}}>
                <b>Nomination Reason :</b>
                {nomination.nominationReason}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box mt={2}>
              <Typography variant="body1" sx={{ marginBottom: '8px',fontSize: isMobile ? '14px' : 'inherit' }}>
                Attachment :
              </Typography>
              {nomination.attachment.length > 0 ? (
                <Box mt={2} display="flex" alignItems="center">
                 <AttachmentGallery attachments={nomination.attachment}   isDetailScreen={true} defaultimg={defaultimg}/>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'gray',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: isMobile ? '12px' : '16px'
                  }}
                >
                  <BlockIcon sx={{ marginRight: 0.5 }} /> No attachment provided
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
        <Button
          onClick={() => handleSetWinner(nomination._id, nomination.categoryId, winnerIdentifier)}
          disabled={nomination.isWinner}
          color="primary"
        >
          Set Winner
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OverviewDialog;
